import { cn } from '@/lib/utils';
import {
  Box,
  Button,
  ButtonProps,
  HStack,
  Heading,
  Stack,
  StackProps,
  Text,
  TextProps,
} from '@chakra-ui/react';
import { ChevronLeft } from 'lucide-react';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

export const CustomH3 = ({
  children,
  headerProps,
  classnames,
}: {
  children: ReactNode;
  headerProps?: TextProps;
  classnames?: string;
}) => {
  return (
    <Heading
      as={'h3'}
      mb={1}
      fontWeight={600}
      fontSize={'1.1rem'}
      color={'text.400'}
      className={cn('tracking-tight', classnames)}
      {...headerProps}
    >
      {children}
    </Heading>
  );
};

export const CustomH2 = ({
  children,
  classnames,
  headerProps,
}: {
  children: ReactNode;
  classnames?: string;
  headerProps?: TextProps;
}) => {
  return (
    <Heading
      as={'h2'}
      mb={1}
      fontWeight={700}
      fontSize={'1.3rem'}
      color={'foreground'}
      className={cn('tracking-tight', classnames)}
      {...headerProps}
    >
      {children}
    </Heading>
  );
};

export const CustomH1 = ({
  children,
  classnames,
  headerProps,
}: {
  children: ReactNode;
  classnames?: string;
  headerProps?: TextProps;
}) => {
  return (
    <Heading
      as={'h1'}
      fontWeight={700}
      fontSize={{ base: '1.6rem', md: '1.8rem', lg: '2.2rem' }}
      color={'#0D0E12'}
      className={cn('tracking-tight', classnames)}
      {...headerProps}
    >
      {children}
    </Heading>
  );
};

export const PageHeader = ({
  title,
  sub,
  boxProps,
  buttonLabel,
  buttonProps,
  onlySmallScreens,
  buttonOnClick,
}: {
  title?: string;
  sub?: string;
  boxProps?: StackProps;
  buttonLabel?: string;
  buttonProps?: ButtonProps;
  onlySmallScreens?: boolean;
  buttonOnClick?: () => void;
}) => {
  return (
    <Box
      w={'full'}
      display={onlySmallScreens ? { base: 'block', lg: 'none' } : undefined}
    >
      <Stack
        mb={{ base: 4, md: 5 }}
        gap={4}
        flexDirection={{ base: 'row' }}
        justifyContent={'flex-start'}
        alignItems={{ base: 'flex-end', md: 'center' }}
        {...boxProps}
      >
        <Box display={{ base: 'block', md: 'none' }}>
          {title ? (
            <Text textStyle={'header1'} mb={1}>
              {title}
            </Text>
          ) : null}
          {sub ? (
            <Text
              display={{ base: 'none', md: 'block' }}
              textStyle={'normal'}
              color={'forground'}
              fontWeight={400}
              opacity={0.8}
            >
              {sub}
            </Text>
          ) : null}
        </Box>
        <Box display={{ base: 'none', md: 'block' }}>
          {title ? (
            <Text textStyle={'header1'} mb={1}>
              {title}
            </Text>
          ) : null}
          {sub ? (
            <Text
              display={{ base: 'none', md: 'block' }}
              textStyle={'normal'}
              color={'foreground'}
              opacity={0.8}
            >
              {sub}
            </Text>
          ) : null}
        </Box>

        <Box ml={'auto'}>
          {buttonLabel ? (
            <Button
              minH={'10px'}
              // size={{ base: "md", xl: "sm" }}
              fontSize={'14px'}
              px={6}
              onClick={buttonOnClick}
              {...buttonProps}
            >
              {buttonLabel}
            </Button>
          ) : null}
        </Box>
      </Stack>

      <Text
        mb={8}
        display={{ base: 'block', md: 'none' }}
        textStyle={'normal'}
        lineHeight={1.3}
        color={'text.140'}
      >
        {sub}
      </Text>
    </Box>
  );
};

export const PageCardBox = ({
  children,
  fullPage,
  noData,
  backButtonLabel,
  header,
}: {
  children: ReactNode;
  fullPage?: boolean;
  noData?: boolean;
  backButtonLabel?: string;
  header?: string;
}) => {
  const navigate = useNavigate();
  return (
    <Box
      w={'100%'}
      border={'1px solid #E4E4EC'}
      borderRadius={'8px'}
      px={{ base: 4, md: 6 }}
      pb={{ base: 8, md: 12 }}
      minHeight={fullPage ? '70vh' : 'auto'}
      pt={noData ? '15vh' : 4}
    >
      {header || backButtonLabel ? (
        <HStack
          borderBottom={'1px solid transparent'}
          borderBottomColor={'stroke.300'}
          w={'full'}
          justifyContent={'space-between'}
          alignItems={'center'}
          pb={2}
          mb={6}
        >
          <Button
            leftIcon={<ChevronLeft />}
            variant={'link'}
            fontSize={'14px'}
            onClick={() => {
              navigate(-1);
            }}
          >
            {!header ? backButtonLabel : <CustomH2>{header}</CustomH2>}
          </Button>
        </HStack>
      ) : null}

      {children}
    </Box>
  );
};

export const TabTitle = ({
  label,
  children,
  stackProps,
  headerProps,
}: {
  label?: string;
  children?: ReactNode;
  stackProps?: StackProps;
  headerProps?: TextProps;
}) => {
  return (
    <HStack
      borderBottom={'1px solid transparent'}
      borderBottomColor={'stroke.300'}
      px={'24px'}
      py={'13px'}
      w={'full'}
      bg={'bg.50'}
      justifyContent={'flex-start'}
      alignItems={'center'}
      {...stackProps}
    >
      {label ? (
        <Text textStyle={'header2'} mb={0} fontWeight={600} {...headerProps}>
          {label}
        </Text>
      ) : null}
      {children ? children : null}
    </HStack>
  );
};
